import React, { useMemo } from "react";
import ReactJson from "@microlink/react-json-view";
import { Box } from "@mui/material";

interface JsonViewerProps {
  data: any;
  maxExpandLevel?: number;
}

export const JsonViewer: React.FC<JsonViewerProps> = ({
  data,
  maxExpandLevel = 3,
}) => {
  // Safe JSON Parsing Logic
  const { jsonData, isValidJson } = useMemo(() => {
    if (!data) return { jsonData: data, isValidJson: false };

    // 1. If it is already an object, return it directly
    if (typeof data === "object" && data !== null) {
      return { jsonData: data, isValidJson: true };
    }

    // 2. If it is a string, try to parse it recursively
    if (typeof data === "string") {
      try {
        // Remove Byte Order Mark (BOM) and whitespace
        let cleaned = data.replace(/^\uFEFF/, "").trim();
        let parsed = cleaned;
        let iterations = 0;
        const maxIterations = 5; // Prevent infinite loops

        // Keep parsing until we get an object or reach max iterations
        while (typeof parsed === "string" && iterations < maxIterations) {
          try {
            const temp = JSON.parse(parsed);
            parsed = temp;
            iterations++;
          } catch {
            // If parsing fails, we've reached the final string
            break;
          }
        }

        // Check if we ended up with a valid object
        if (typeof parsed === "object" && parsed !== null) {
          return { jsonData: parsed, isValidJson: true };
        }

        // If after all parsing attempts it's still a string, return as plain text
        return { jsonData: parsed, isValidJson: false };
      } catch (e) {
        // If parsing fails, treat it as plain text
        return { jsonData: data, isValidJson: false };
      }
    }

    // Fallback for other types (numbers, booleans)
    return { jsonData: data, isValidJson: false };
  }, [data]);

  // RENDER: If valid JSON, show the tree view. If not, show plain text.
  if (!isValidJson && typeof jsonData !== "object") {
    return (
      <Box
        sx={{
          fontFamily: "monospace",
          fontSize: "0.75rem",
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
          color: "#333",
          p: 1,
        }}
      >
        {String(jsonData)}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        fontFamily: "Consolas, Monaco, 'Courier New', monospace",
        fontSize: "0.75rem !important",
        "& .react-json-view": {
          fontSize: "0.75rem !important",
          fontFamily: "Consolas, Monaco, 'Courier New', monospace !important",
          lineHeight: "1.5 !important",
        },
      }}
    >
      <ReactJson
        src={jsonData}
        name={false}
        collapsed={maxExpandLevel}
        displayDataTypes={false}
        displayObjectSize={true}
        enableClipboard={true}
        indentWidth={2}
        collapseStringsAfterLength={100}
        theme={{
          base00: "#ffffff",
          base01: "#f5f5f5",
          base02: "#e0e0e0",
          base03: "#bdbdbd",
          base04: "#9e9e9e",
          base05: "#616161",
          base06: "#424242",
          base07: "#212121",
          base08: "#f44336",
          base09: "#ff9800",
          base0A: "#ffc107",
          base0B: "#4caf50",
          base0C: "#00bcd4",
          base0D: "#2196f3",
          base0E: "#9c27b0",
          base0F: "#795548",
        }}
        style={{
          fontSize: "0.75rem",
          fontFamily: "Consolas, Monaco, 'Courier New', monospace",
          padding: 0,
          backgroundColor: "transparent",
        }}
      />
    </Box>
  );
};
